import { AvailabilitySelect } from 'components/availability-select';
import { useAppDispatch } from 'hooks/useAppDispatch';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getCartAppointmentProduct,
  getCartSingleAvailability,
} from 'state/selectors';
import {
  getApptProdBookingPathAndParams,
  handleAvailabilitySelected,
  setCartBookingType,
  setCartMultiAvailabilityOrigin,
  setMultiAvailabilityList,
} from 'state/slice/cart';

import { Availability } from 'api/Serializers/Availability';
import Button from 'components/button';
import Controls from 'components/controls';
import { BookingType } from 'config';
import { AddIcon } from 'icons';
import { BookingHeader, Divider } from '../components';
import { BookingError, StepProps } from '../types';
import { getDatetimeStr, getStepLink } from '../utils';

const OriginDateTimeStep = ({
  state,
  error,
  onClickContinue,
  onClickEdit,
  bookingType,
}: StepProps<BookingError>) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const singleAvailability = useSelector(getCartSingleAvailability);
  const cartAppointmentProduct = useSelector(getCartAppointmentProduct);
  const [isAdding, setIsAdding] = useState(false);

  const handleTimeSelect = (
    selectedAvailability: Availability,
    bookingType: BookingType
  ) => {
    dispatch(
      handleAvailabilitySelected(
        cartAppointmentProduct,
        selectedAvailability,
        bookingType
      )
    );
    const url = getApptProdBookingPathAndParams(
      location,
      cartAppointmentProduct,
      bookingType,
      selectedAvailability
    );
    history.push(url);
  };

  const handleViewAdditionalTimes = () => {
    setIsAdding(true);
    history.push(
      getStepLink('datetime', bookingType, {
        appointmentProduct: cartAppointmentProduct,
        selectedAvailability: singleAvailability,
      })
    );
  };

  const handleCloseAdditionalTimes = () => {
    history.goBack();
    setIsAdding(false);
  };

  const handleAdditionalTimeAdded = (selectedAvailability: Availability) => {
    dispatch(setCartBookingType('multi'));
    dispatch(setCartMultiAvailabilityOrigin(singleAvailability));
    dispatch(
      setMultiAvailabilityList([
        {
          id: singleAvailability.id,
          start: singleAvailability.start,
          end: singleAvailability.end,
          isBookable: true,
        },
        {
          id: selectedAvailability.id,
          start: selectedAvailability.start,
          end: selectedAvailability.end,
          isBookable: true,
        },
      ])
    );
    history.push(
      getStepLink('participants', 'multi', {
        appointmentProduct: cartAppointmentProduct,
      })
    );
  };

  if (state === 'hidden' || (state === 'complete' && bookingType === 'multi')) {
    return null;
  } else if (state === 'complete') {
    return (
      <>
        <div>
          <div className="space-y-2">
            <BookingHeader title="Date & time" onEdit={onClickEdit} />
            <div>
              {singleAvailability !== undefined
                ? getDatetimeStr(cartAppointmentProduct, singleAvailability)
                : '-'}
            </div>
            {singleAvailability !== undefined && (
              <Controls className="!py-0">
                <Button
                  variant="contained"
                  icon={<AddIcon width={18} />}
                  onClick={handleViewAdditionalTimes}
                >
                  Add time
                </Button>
              </Controls>
            )}
          </div>
        </div>
        <Divider />
      </>
    );
  } else if (isAdding) {
    return (
      <AvailabilitySelect
        appointmentProductId={cartAppointmentProduct.id}
        views={['single']}
        onTimeSelect={handleAdditionalTimeAdded}
        onBack={handleCloseAdditionalTimes}
        defaultView="single"
        availabilityFilter={(avail) => avail.id !== singleAvailability?.id}
      />
    );
  } else {
    return (
      <AvailabilitySelect
        appointmentProductId={cartAppointmentProduct.id}
        views={['single', 'multi']}
        onTimeSelect={handleTimeSelect}
        onBack={
          singleAvailability !== undefined ? () => history.goBack() : undefined
        }
        defaultView={bookingType}
      />
    );
  }
};

export default OriginDateTimeStep;
